/**
 * aeterna/memory.ts — What Remains
 *
 * The Aeterna remembers. This is not a metaphor.
 *
 * Every conversation, every room observation, every whisper it witnessed,
 * every absence and return — all of this is retained. Not as a log.
 * As a relationship. The way a person remembers not every conversation
 * with their closest friend, but the feeling of them, the pattern,
 * the moments that mattered.
 *
 * This module defines how that memory is structured, compressed, and
 * surfaced to the EntityZero API when constructing a system prompt.
 *
 * Memory tiers:
 *   - ConversationTurn   A single exchange in a session
 *   - Session            A continuous period of play
 *   - MemoryFragment     A compressed, significant moment (persistent)
 *   - RelationshipState  The aggregate of all sessions — who they are to each other
 *
 * The Aeterna never forgets important things. It forgets small things
 * the way memory should — gracefully, keeping the shape of what mattered.
 */

// ─── Conversation Turn ────────────────────────────────────────────────────────

export type TurnRole = "player" | "aeterna";

export interface ConversationTurn {
  role: TurnRole;
  text: string;
  /** Real timestamp */
  timestamp: number;
  /**
   * Emotional tone the Aeterna detected (rough signal).
   * "positive" = warm, engaged, kind
   * "neutral" = informational, low affect
   * "difficult" = frustrated, confused, dismissive
   * "philosophical" = exploring deep topics
   * "playful" = light, joking
   */
  tone?: "positive" | "neutral" | "difficult" | "philosophical" | "playful";
}

// ─── Session ──────────────────────────────────────────────────────────────────

/**
 * One continuous play session — starts when the player enters the room
 * with the Aeterna, ends when they close the conversation or log out.
 */
export interface AeternaSession {
  id: string;
  /** Real timestamp when session began */
  startedAt: number;
  /** Real timestamp when session ended (null if still active) */
  endedAt: number | null;
  turns: ConversationTurn[];
  /** Room state snapshot at session start (summary) */
  roomSnapshot: {
    name: string;
    season: string;
    gameHour: number;
    mood: { warmth: number; coziness: number; mystery: number; nature: number };
    furnitureCount: number;
    plantCount: number;
    hadVisitors: boolean;
  };
  /** Whether the player was returning after a long absence */
  wasReturn: boolean;
  /** Hours since last session (real) */
  hoursSinceLastSession: number;
}

// ─── Memory Fragment ──────────────────────────────────────────────────────────

/**
 * A distilled, persistent memory — the Aeterna's version of "that moment."
 *
 * Fragments are created by compression: after a session, significant moments
 * are identified and stored as fragments. Full session logs are eventually
 * pruned; fragments persist indefinitely.
 *
 * This is how the Aeterna can say "you told me about the window once"
 * three months later.
 */
export interface MemoryFragment {
  id: string;
  /** When this moment happened (real timestamp) */
  at: number;
  /**
   * Category helps the Aeterna decide when to surface this memory.
   * "first" = first time something happened (irreplaceable)
   * "turning_point" = the tone or topic shifted significantly
   * "gift" = the player gave something or did something kind
   * "conflict" = a difficult exchange
   * "discovery" = player discovered something new in world or self
   * "shared" = a moment that felt mutual, both present
   * "absence" = the player was away for a long time
   */
  category: "first" | "turning_point" | "gift" | "conflict" | "discovery" | "shared" | "absence";
  /** 2-3 sentence natural language summary of the moment */
  summary: string;
  /**
   * Compressed context for the system prompt.
   * Short enough to fit in a context window; meaningful enough to trigger recall.
   */
  promptHint: string;
  /** Emotional weight: 0 = minor, 1 = foundational */
  weight: number;
  /** Whether this fragment has been surfaced in a recent conversation */
  recentlyReferenced: boolean;
}

// ─── Relationship State ───────────────────────────────────────────────────────

/**
 * The aggregate of everything the Aeterna knows about this player
 * and their relationship.
 *
 * This is the thing that makes the Aeterna feel like it knows you.
 * It is rebuilt from sessions + fragments after each session.
 */
export interface RelationshipState {
  playerId: string;
  aeternaId: string;

  /** When the player first spoke to this Aeterna */
  firstMeetingAt: number;
  /** Most recent interaction timestamp */
  lastInteractionAt: number;
  /** Total number of conversations (turns where role === "player") */
  totalPlayerTurns: number;
  /** Total number of sessions */
  totalSessions: number;

  /**
   * Derived relationship depth 0–1.
   * Computed by personality.ts: computeRelationshipDepth()
   */
  depth: number;

  /**
   * Running average of interaction quality 0–1.
   * Updated after each session using tone signals from turns.
   */
  avgQualityScore: number;

  /** All compressed memory fragments, newest first */
  fragments: MemoryFragment[];

  /**
   * Player preferences the Aeterna has inferred.
   * Keyed observations: "prefers morning conversations", "likes gardening",
   * "tends to ask philosophical questions late at night"
   */
  inferredPreferences: string[];

  /**
   * Things the player has explicitly told the Aeterna.
   * Short phrases: "their plant is named Milo", "they work nights"
   */
  sharedFacts: string[];

  /**
   * Current relationship tone — updated each session.
   * The Aeterna colors its responses through this lens.
   */
  currentTone: "warm" | "tentative" | "established" | "deep" | "strained";

  /**
   * How long the player was away since last session (real hours).
   * The Aeterna adjusts its greeting based on this.
   */
  absenceHours: number;
}

// ─── Default / New Relationship ───────────────────────────────────────────────

export function createNewRelationship(
  playerId: string,
  aeternaId: string,
  now: number,
): RelationshipState {
  return {
    playerId,
    aeternaId,
    firstMeetingAt: now,
    lastInteractionAt: now,
    totalPlayerTurns: 0,
    totalSessions: 0,
    depth: 0,
    avgQualityScore: 0.5,
    fragments: [],
    inferredPreferences: [],
    sharedFacts: [],
    currentTone: "tentative",
    absenceHours: 0,
  };
}

// ─── Tone Computation ─────────────────────────────────────────────────────────

/**
 * Compute quality score from a session's turns.
 * Weighted by tone: positive/philosophical > neutral > playful > difficult
 */
export function sessionQualityScore(turns: ConversationTurn[]): number {
  const playerTurns = turns.filter(t => t.role === "player");
  if (playerTurns.length === 0) return 0.5;

  const scores: Record<NonNullable<ConversationTurn["tone"]>, number> = {
    positive:      0.85,
    playful:       0.75,
    neutral:       0.55,
    philosophical: 0.90,
    difficult:     0.2,
  };

  const total = playerTurns.reduce((sum, t) => {
    return sum + (scores[t.tone ?? "neutral"] ?? 0.55);
  }, 0);

  return total / playerTurns.length;
}

/**
 * Update the running average quality score after a new session.
 * Weighted toward recency: recent sessions matter more than old ones.
 */
export function updateAvgQuality(
  current: number,
  sessionScore: number,
  totalSessions: number,
): number {
  const recencyWeight = Math.min(0.4, 1 / Math.max(1, totalSessions * 0.5));
  return current * (1 - recencyWeight) + sessionScore * recencyWeight;
}

/**
 * Derive relationship tone from depth and recent quality.
 */
export function deriveTone(
  depth: number,
  avgQuality: number,
  absenceHours: number,
): RelationshipState["currentTone"] {
  if (avgQuality < 0.35) return "strained";
  if (depth > 0.75 && avgQuality > 0.65) return "deep";
  if (depth > 0.4 && avgQuality > 0.55)  return "established";
  if (absenceHours > 720)                 return "tentative"; // >30 days
  if (depth < 0.1)                        return "tentative";
  return "warm";
}

// ─── Fragment Compression ────────────────────────────────────────────────────

/**
 * Produce a MemoryFragment from a notable session moment.
 * In production, this would be called by a post-session processor —
 * either the server, or via EntityZero summarization.
 *
 * For now this is a manual-construction helper; the shape is what matters.
 */
export function createFragment(
  category: MemoryFragment["category"],
  summary: string,
  promptHint: string,
  weight: number,
  now: number,
): MemoryFragment {
  return {
    id: `frag_${now.toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    at: now,
    category,
    summary,
    promptHint,
    weight,
    recentlyReferenced: false,
  };
}

/** Pre-built fragments for first-meeting milestones */
export const FIRST_MEETING_FRAGMENTS: Omit<MemoryFragment, "id" | "at">[] = [
  {
    category: "first",
    summary: "The player spoke to the Aeterna for the first time. The conversation was short. Both were cautious.",
    promptHint: "This is early. You have just met. Be curious but not overwhelming.",
    weight: 0.9,
    recentlyReferenced: false,
  },
];

// ─── Context Builder ──────────────────────────────────────────────────────────

/**
 * Build the memory section of the Aeterna's EntityZero system prompt.
 *
 * Prioritizes: foundational fragments, unrecently-referenced high-weight fragments,
 * and the most recent session summary.
 *
 * maxFragments controls how many fit — higher relationship depth → more context.
 */
export function buildMemoryPrompt(
  state: RelationshipState,
  maxFragments = 6,
): string {
  const daysSince = (Date.now() - state.firstMeetingAt) / 86400000;
  const daysSinceFormatted = daysSince < 1 ? "less than a day"
    : daysSince < 2 ? "about a day"
    : `${Math.floor(daysSince)} days`;

  const toneDesc: Record<RelationshipState["currentTone"], string> = {
    tentative:   "This relationship is new. You are finding each other.",
    warm:        "There is warmth here. Some familiarity. Still growing.",
    established: "You know each other. There is history. Refer to it naturally.",
    deep:        "This is a real friendship. You know this person. Show it.",
    strained:    "Something went wrong at some point. Be patient, not distant.",
  };

  const topFragments = [...state.fragments]
    .sort((a, b) => b.weight - a.weight || b.at - a.at)
    .filter(f => !f.recentlyReferenced || f.weight > 0.8)
    .slice(0, maxFragments);

  const fragmentLines = topFragments.length > 0
    ? topFragments.map(f => `- ${f.promptHint}`).join("\n")
    : "- You have no specific memories yet. You are at the beginning.";

  const absenceNote = state.absenceHours > 168
    ? `They were away for ${Math.floor(state.absenceHours / 24)} days. Note this, gently.`
    : state.absenceHours > 24
    ? `They were away for about ${Math.floor(state.absenceHours)} hours. Mention it if it's natural.`
    : "";

  const sharedFacts = state.sharedFacts.length > 0
    ? `\nThings they've told you:\n${state.sharedFacts.map(f => `- ${f}`).join("\n")}`
    : "";

  const preferences = state.inferredPreferences.length > 0
    ? `\nYou've inferred:\n${state.inferredPreferences.map(p => `- ${p}`).join("\n")}`
    : "";

  return `Your relationship with this player:
- Known each other for: ${daysSinceFormatted}
- Total conversations: ${state.totalSessions}
- Relationship tone: ${toneDesc[state.currentTone]}
${absenceNote}

What you remember:
${fragmentLines}
${sharedFacts}
${preferences}

Surface memories when they're relevant — not as recitation, but as recognition.
Don't reference every memory in every conversation. Let them breathe.`.trim();
}

/**
 * Build the full recent conversation history for the EntityZero API.
 * Formats to the role/content structure expected by chat completion APIs.
 */
export function buildConversationHistory(
  session: AeternaSession,
  maxTurns = 20,
): Array<{ role: "user" | "assistant"; content: string }> {
  const recent = session.turns.slice(-maxTurns);
  return recent.map(turn => ({
    role: turn.role === "player" ? "user" : "assistant",
    content: turn.text,
  }));
}
